import {handleActions, type Action} from 'redux-actions';
import moment from 'moment-timezone';
import {Record, List} from 'immutable';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import * as SessionActions from 'web-app/react/entities/sessions/actions';
import * as ProductActions from 'web-app/react/entities/products/actions';
import * as PublicFundingModalActions from 'web-app/react/modals-container/choose-public-funding-modal/actions';

import * as Actions from './actions';
import pricingReducer from './plan-pricing/reducer';
import weekdaysReducer from './weekdays/reducer';
import editWeekdaysReducer from './edit-weekdays/reducer';
import editPlanReducer from './edit-plan-reducer';
import type {ManagePlanState} from './types/state-types';
import type {ManagePlanPayload} from './types/reducer-types';

const ManagePlanState = Record<ManagePlanState>({
    open: false,
    showEdit: false,
    canEdit: true,
    isPlanRulesEnabled: false,
    isAutomaticChargesEnabled: false,
    hasPreventDoubleBookingEnabled: false,
    supportsRuleSets: false,
    overrideSavePlan: undefined,
    overrideSaveText: undefined,
    institutionId: undefined,
    childId: undefined,
    validOn: undefined,
    validTo: undefined,
    canEditValidOn: true,
    canEditValidTo: false,
    validOnMustBeBefore: undefined,
    validOnMustBeAfter: undefined,
    validToMustBeBefore: undefined,
    validToMustBeAfter: undefined,
    pricingGroups: List(),
    ruleGroups: List(),
    billingProfiles: List(),
    attendanceSchedules: List(),
    pricingGroupBehaviors: new BehaviorsCollection([]),
    originalPlanUsedSessions: List(),
    publicFundingPreviewPlanStates: List(),
    originalPlanUsedProducts: List(),
    originalPlanBehaviors: new BehaviorsCollection([]),
    planBehaviors: new BehaviorsCollection([]),
    sessionBehaviors: undefined, // Defined in separate reducer
    productBehaviors: undefined, // Defined in separate reducer
    didFetchData: false,
    fetchingData: false,
    didFetchPreview: false,
    fetchingPreview: false,
    submitting: false,
    editPlan: null,
    pricing: undefined,
    editWeekdays: undefined,
    weekdays: undefined,
    trackingContext: undefined,
    hasOnlyViewed: true,
    templateMode: false,
    showBillingProfileModalForPlanPartId: undefined,
    planStates: undefined,
    onAfterSave: undefined,
});

const updateFromPlan = (
    state: Record<ManagePlanState> & Readonly<ManagePlanState>,
    payload: ManagePlanPayload | undefined,
) => {
    if (!payload) {
        return state;
    }

    const planBehaviors = payload.editPlan.behaviors;

    // We must subtract a day from the validTo we send to the backend
    const validTo = payload.editPlan.to
        ? payload.editPlan.to.clone().subtract(1, 'day').format('YYYY-MM-DD')
        : undefined;

    const planStates = payload.editPlan.planStates;
    return state.merge({
        open: true,
        validOn: payload.editPlan.from
            ? payload.editPlan.from.format('YYYY-MM-DD')
            : defaultValidOn(payload.validOnMustBeAfter),
        publicFundingPreviewPlanStates: planStates,
        originalPlanBehaviors: planBehaviors,
        validTo,
        originalPlanUsedSessions: payload.planUsedSessions,
        originalPlanUsedProducts: payload.planUsedProducts,
        validOnMustBeBefore: payload.validOnMustBeBefore,
        validOnMustBeAfter: payload.validOnMustBeAfter,
        validToMustBeBefore: payload.validToMustBeBefore,
        validToMustBeAfter: payload.validToMustBeAfter,
        canEditValidOn: Boolean(payload.canEditValidOn),
        canEditValidTo: Boolean(payload.canEditValidTo),
        institutionId: payload.institutionId,
        childId: payload.childId,
        trackingContext: payload.trackingContext,
        overrideSavePlan: payload.overrideSavePlan,
        overrideSaveText: payload.overrideSaveText,
        templateMode: payload.templateMode,
        showEdit: Boolean(payload.templateMode),
    });
};

const createOrUpdateSuccess = (state: any, payload: ManagePlanPayload | undefined) => {
    if (!payload) {
        return state;
    }

    const planBehaviors = payload.plan.behaviors;
    return state.merge({
        showEdit: false,
        originalPlanUsedSessions: List(),
        originalPlanUsedProducts: List(),
        submitting: false,
        planBehaviors,
        originalPlanBehaviors: planBehaviors,
        hasOnlyViewed: false,
    });
};

const managePlan = handleActions(
    {
        [Actions.showCreatePlan.type]: (state, {payload}: Action<ManagePlanPayload>) => {
            if (!payload) {
                return state;
            }

            const planStates = payload.editPlan ? payload.editPlan.planStates : List();
            return state.merge({
                showEdit: true,
                open: true,
                validOn: defaultValidOn(payload.validOnMustBeAfter),
                planBehaviors: payload.behaviors || new BehaviorsCollection([]),
                validTo: payload.validTo,
                validOnMustBeBefore: payload.validOnMustBeBefore,
                validOnMustBeAfter: payload.validOnMustBeAfter,
                validToMustBeBefore: payload.validToMustBeBefore,
                validToMustBeAfter: payload.validToMustBeAfter,
                institutionId: payload.institutionId,
                childId: payload.childId,
                canEditValidOn: payload.canEditValidOn ?? true,
                canEditValidTo: payload.canEditValidTo ?? true,
                publicFundingPreviewPlanStates: planStates,
                originalPlanUsedSessions: payload.editPlan ? payload.planUsedSessions : List(),
                originalPlanUsedProducts: payload.editPlan ? payload.planUsedProducts : List(),
                trackingContext: payload.trackingContext,
                overrideSavePlan: payload.overrideSavePlan,
                overrideSaveText: payload.overrideSaveText,
                templateMode: payload.templateMode,
                onAfterSave: payload.onAfterSave,
            });
        },

        [Actions.insertPlan.type]: (state, {payload}: Action<ManagePlanPayload>) => {
            return updateFromPlan(state, payload).merge({
                showEdit: true,
            });
        },
        [Actions.showEditPlan.type]: (state, {payload}: Action<ManagePlanPayload>) => {
            if (!payload) {
                return state;
            }

            return updateFromPlan(state, payload).merge({
                planBehaviors: payload.editPlan.behaviors,
            });
        },
        [Actions.simpleViewPlan.type]: (state, {payload}: Action<ManagePlanPayload>) => {
            if (!payload) {
                return state;
            }

            return updateFromPlan(state, payload).merge({
                planBehaviors: payload.editPlan.behaviors,
                canEdit: false,
                showEdit: Boolean(payload.isEdit),
                overrideSavePlan: payload.overrideSavePlan,
            });
        },
        [Actions.planPeriodDidChange.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            state.merge({
                validOn: payload?.validOn,
                validTo: payload?.validTo,
            }),
        [Actions.startFetchingData.type]: state => state.merge({fetchingData: true}),
        [Actions.fetchManagePlanData.type]: state => state.merge({fetchingData: true}),
        [Actions.fetchManagePlanDataFailed.type]: state =>
            state.merge({
                fetchingData: false,
            }),
        [Actions.fetchManagePlanDataSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) => {
            if (!payload) {
                return state;
            }

            return state.merge({
                pricingGroupBehaviors: payload.pricingGroupBehaviors,
                pricingGroups: payload.pricingGroups,
                billingProfiles: payload.billingProfiles,
                attendanceSchedules: payload.attendanceSchedules,
                didFetchData: true,
                fetchingData: false,
            });
        },
        [Actions.fetchRuleGroupsSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            state.merge({
                ruleGroups: payload?.ruleGroupOptions.ruleGroupOptions,
            }),
        [Actions.isPlanRulesEnabledSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            state.merge({
                isPlanRulesEnabled: payload?.isPlanRulesEnabled?.isPlanRulesEnabled,
            }),
        [Actions.isAutomaticChargesEnabledSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            state.merge({
                isAutomaticChargesEnabled: payload?.isAutomaticChargesEnabled?.isAutomaticChargesEnabled,
            }),
        [Actions.hasPreventDoubleBookingEnabledSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            state.merge({
                hasPreventDoubleBookingEnabled: payload?.hasPreventDoubleBookingEnabled?.hasPreventDoubleBookingEnabled,
            }),
        [Actions.supportsRuleSetsSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            state.merge({
                supportsRuleSets: payload?.supportsRuleSets,
            }),
        [Actions.createPlan.type]: state => state.merge({submitting: true}),
        [Actions.createPlanSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            createOrUpdateSuccess(state, payload),
        [Actions.createPlanFailed.type]: state => state.merge({submitting: false}),
        [Actions.startPreviewPlan.type]: state => state.merge({fetchingPreview: true}),
        [Actions.previewPlanSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) => {
            if (!payload) {
                return state;
            }

            const planStates = payload.plan.planStates;
            return state.merge({
                fetchingPreview: false,
                didFetchPreview: true,
                publicFundingPreviewPlanStates: planStates,
                planBehaviors: payload.plan.behaviors,
            });
        },
        [Actions.previewPlanFailed.type]: state => state.merge({didFetchPreview: false, fetchingPreview: false}),
        [Actions.previewPlanAborted.type]: state => state.merge({didFetchPreview: false, fetchingPreview: false}),
        [Actions.previewPlanPublicFunding.type]: state => state.merge({fetchingPreview: true}),
        [Actions.previewPlanPublicFundingSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            state.merge({
                publicFundingPreviewPlanStates: payload?.plan.planStates,
                fetchingPreview: false,
            }),
        [PublicFundingModalActions.hide.type]: state =>
            state.merge({
                publicFundingPreviewPlanStates: state.planStates,
            }),
        [Actions.previewPlanPublicFundingFailed.type]: state => state.merge({fetchingPreview: false}),
        [Actions.previewPlanPublicFundingAborted.type]: state => state.merge({fetchingPreview: false}),
        [Actions.updatePlan.type]: state => state.merge({submitting: true}),
        [Actions.updatePlanSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            createOrUpdateSuccess(state, payload),
        [Actions.updatePlanFailed.type]: state => state.merge({submitting: false}),
        [Actions.showEdit.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            state.merge({
                showEdit: payload?.show,
            }),
        [Actions.updateToLaterPriceChange.type]: (state, {payload}: Action<ManagePlanPayload>) =>
            state.merge({
                showEdit: true,
                validOn: payload?.validFrom,
            }),
        [Actions.closeManagePlan.type]: () => {
            return new ManagePlanState();
        },
        [Actions.manuallyCloseModal.type]: () => {
            return new ManagePlanState();
        },
        [Actions.openBillingProfileModal.type]: (state, {payload}: Action<ManagePlanPayload>) => {
            return state.merge({
                showBillingProfileModalForPlanPartId: payload?.planPartId,
            });
        },
        [Actions.closeBillingProfileModal.type]: state => {
            return state.merge({
                showBillingProfileModalForPlanPartId: undefined,
            });
        },
        [Actions.setBillingProfile.type]: state => {
            return state.merge({
                showBillingProfileModalForPlanPartId: undefined,
            });
        },
    },
    new ManagePlanState(),
);

const sessionBehaviors = handleActions(
    {
        [SessionActions.fetchAllSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) => payload?.behaviors,
    },
    new BehaviorsCollection([]),
);

const productBehaviors = handleActions(
    {
        [ProductActions.fetchAllSuccess.type]: (state, {payload}: Action<ManagePlanPayload>) => payload?.behaviors,
    },
    new BehaviorsCollection([]),
);

const defaultValidOn = (validOnMustBeAfter: ManagePlanPayload['validOnMustBeAfter']) => {
    return validOnMustBeAfter || moment().format('YYYY-MM-DD');
};

const reducer = (state, action) => {
    const managePlanObject = managePlan(state, action);

    return managePlanObject.merge({
        editPlan: editPlanReducer(state ? state.editPlan : undefined, action),
        sessionBehaviors: sessionBehaviors(state ? state.sessionBehaviors : undefined, action),
        productBehaviors: productBehaviors(state ? state.productBehaviors : undefined, action),
        pricing: pricingReducer(state ? state.pricing : undefined, action),
        editWeekdays: editWeekdaysReducer(state ? state.editWeekdays : undefined, action),
        weekdays: weekdaysReducer(state ? state.weekdays : undefined, action),
    });
};

export default reducer;
